
import { useState } from "react";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Sparkles, Image as ImageIcon, Info } from "lucide-react";
import ImageGenerator from "@/components/sections/admin/image-studio/ImageGenerator";
import ImageEditor from "@/components/sections/admin/image-studio/ImageEditor";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";

const AdminImageStudio = () => {
  const [activeTab, setActiveTab] = useState<string>("generate");
  const [isGuideOpen, setIsGuideOpen] = useState(true);

  return (
    <div className="min-h-screen py-24">
      <div className="max-w-6xl mx-auto px-4">
        <div className="flex items-center gap-2 mb-8">
          <Sparkles className="h-8 w-8 text-primary" />
          <h1 className="text-3xl font-bold">Image Studio</h1>
        </div>

        <p className="text-gray-500 mb-8">
          Create and edit images using AI. Generate new images from text prompts or edit existing images with instructions.
        </p>

        <Collapsible open={isGuideOpen} onOpenChange={setIsGuideOpen} className="mb-8">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Info className="h-5 w-5 text-primary" />
              <h2 className="text-xl font-semibold">Seed Potato Image Guide</h2>
            </div>
            <CollapsibleTrigger asChild>
              <Button variant="ghost" size="sm">
                {isGuideOpen ? "Hide Guide" : "Show Guide"}
              </Button>
            </CollapsibleTrigger>
          </div>
          
          <CollapsibleContent>
            <Card className="mt-4">
              <CardContent className="pt-6">
                <div className="space-y-4">
                  <div>
                    <h3 className="font-medium text-lg mb-2">Seed Potato Growth Stages</h3>
                    <ul className="list-disc pl-5 space-y-2">
                      <li>
                        <span className="font-medium">Planting (Spring):</span> Freshly tilled soil with rows, light brown/gray soil color,
                        possibly with some machinery or workers present in Scottish fields
                      </li>
                      <li>
                        <span className="font-medium">Growing Season (Early-Mid Summer):</span> Green leafy plants in neat rows with
                        white or purple flowers, plants about 1-2 feet tall, distinctly different from grain crops
                      </li>
                      <li>
                        <span className="font-medium">Haulm Destruction (Late Summer):</span> Browning/yellowing foliage after chemical 
                        desiccation or mechanical flailing, NOT bright green at this stage
                      </li>
                      <li>
                        <span className="font-medium">Harvest (Autumn):</span> Brown, dry soil with harvesting machinery, exposed light
                        brown/yellowish potatoes, NO green plants visible, sometimes reddish-brown soil in Scotland
                      </li>
                      <li>
                        <span className="font-medium">Post-Harvest:</span> Bare soil with potato remnants, storage facilities, grading
                        equipment in sheds with workers in typical Scottish agricultural settings
                      </li>
                    </ul>
                  </div>
                  
                  <div>
                    <h3 className="font-medium text-lg mb-2">Accurate Prompting Tips</h3>
                    <ul className="list-disc pl-5 space-y-2">
                      <li>
                        <span className="font-medium">Be Stage-Specific:</span> Always specify "seed potato fields during harvest 
                        with brown/bare soil" not just "potato fields" (which defaults to green growing plants)
                      </li>
                      <li>
                        <span className="font-medium">Include Scottish Context:</span> Mention "Scottish seed potato farm", "Scottish 
                        Highlands agriculture", or "Scottish rural landscape" for geographical accuracy
                      </li>
                      <li>
                        <span className="font-medium">Specify Scale:</span> Distinguish between "close-up of seed potatoes being 
                        inspected" vs. "aerial view of Scottish seed potato fields during harvest"
                      </li>
                      <li>
                        <span className="font-medium">Weather & Lighting:</span> Scottish conditions often include "overcast sky", 
                        "misty conditions", or "soft diffused light" rather than bright sunny conditions
                      </li>
                      <li>
                        <span className="font-medium">Equipment:</span> Mention specific equipment like "potato harvester machines", 
                        "seed potato grading equipment", or "Scottish seed potato storage facilities"
                      </li>
                    </ul>
                  </div>
                  
                  <div>
                    <h3 className="font-medium text-lg mb-2">Sample Prompts</h3>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="bg-gray-50 p-3 rounded-md">
                        <p className="font-medium text-sm mb-1">Harvest Time:</p>
                        <p className="text-sm italic">"Scottish seed potato harvest in Perthshire, rows of exposed potatoes in reddish-brown soil, cloudy autumn day, harvesting machinery in action, NO green foliage"</p>
                      </div>
                      <div className="bg-gray-50 p-3 rounded-md">
                        <p className="font-medium text-sm mb-1">Inspection Process:</p>
                        <p className="text-sm italic">"Indoor Scottish seed potato inspection facility, workers carefully examining harvested seed potatoes on conveyor belt, bright industrial lighting, quality control process"</p>
                      </div>
                      <div className="bg-gray-50 p-3 rounded-md">
                        <p className="font-medium text-sm mb-1">Storage Stage:</p>
                        <p className="text-sm italic">"Scottish seed potato storage shed interior, wooden crates filled with graded seed potatoes, temperature controlled environment, agricultural workers monitoring conditions"</p>
                      </div>
                      <div className="bg-gray-50 p-3 rounded-md">
                        <p className="font-medium text-sm mb-1">Field Preparation:</p>
                        <p className="text-sm italic">"Scottish farm preparing fields for seed potato planting, tractor creating furrows in freshly tilled soil, early spring in Angus region, misty morning light on agricultural landscape"</p>
                      </div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          </CollapsibleContent>
        </Collapsible>

        <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
          <TabsList className="mb-6 w-full justify-start">
            <TabsTrigger value="generate" className="flex items-center gap-2">
              <Sparkles className="h-4 w-4" />
              Generate Images
            </TabsTrigger>
            <TabsTrigger value="edit" className="flex items-center gap-2">
              <ImageIcon className="h-4 w-4" />
              Edit Images
            </TabsTrigger>
          </TabsList>
          
          <TabsContent value="generate">
            <ImageGenerator />
          </TabsContent>
          
          <TabsContent value="edit">
            <ImageEditor />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default AdminImageStudio;
